<template>
  <main id="app" class="min-vh-100 d-flex align-items-center position-relative" :class="[cssClass, `locale-${locale}`]">
      <div
          class="background-image-container"
          :class="{ 'has-image': event && event.backgroundImageHtml && cssClass === 'portal' }"
      >
        <div
            v-if="event && event.backgroundImageHtml && cssClass === 'portal'"
            v-html="event.backgroundImageHtml"
            class="background-image"
        ></div>
      </div>
    <div class="container p-0">
      <div class="main-card card position-relative rounded-4">
        <header
            class="header rounded-top-4 px-4 py-4 d-flex screen-content"
            :class="[event ? 'justify-content-between' : '', { 'border-bottom py-4': !showSteps }]">
          <locker-company-logo></locker-company-logo>
          <div class="text-end d-flex flex-column ps-2" v-if="event">
            <div class="fw-semibold">{{ event.name }}</div>
            <small class="opacity-75" v-if="booking && booking.period">
              <span v-if="getPeriodText(booking.period, 'name')">
                {{ getPeriodText(booking.period, 'name') }}
                {{ totalAmount > 0 ? '-' : ''}}
              </span>
              <span v-if="totalAmount > 0">{{ $n(totalAmount, 'currency') }}</span>
            </small>
          </div>
        </header>

        <nav
            class="stepper d-flex justify-content-center align-items-center gap-1 gap-sm-2 px-2 py-3" v-if="showSteps">
          <div class="d-flex align-items-center gap-2">
            <div
                class="number rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                :class="{ 'bg-primary text-white': step === 1, 'bg-success bg-opacity-50': step > 1 }"
            >
              <span v-if="step > 1">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-check"
                     viewBox="0 0 16 16"><path fill="white"
                                               d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425z"/></svg>
              </span>
              <span v-else>1</span>
            </div>
            <span :class="{ 'text-success': step > 1 }">{{ $t('steps.step1') }}</span>
            <div class="divider mx-1"></div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <div
                class="number rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                :class="{ 'bg-primary text-white': step === 2, 'bg-black bg-opacity-10': step < 2, 'bg-success bg-opacity-50': step > 2 }"
            >
              <span v-if="step > 2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-check"
                     viewBox="0 0 16 16"><path fill="white"
                                               d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425z"/></svg>
              </span>
              <span v-else>2</span>
            </div>
            <span :class="{ 'text-black-50': step < 2, 'text-success': step > 2 }">{{ $t('steps.step2') }}</span>
            <div class="divider mx-1"></div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <div
                class="number rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                :class="{ 'bg-primary text-white': step === 3, 'bg-black bg-opacity-10': step < 3, 'bg-success bg-opacity-50': step > 3 }"
            >
              <span v-if="step > 3">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-check"
                     viewBox="0 0 16 16"><path fill="white"
                                               d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425z"/></svg>
              </span>
              <span v-else>3</span>
            </div>
            <span :class="{ 'text-black-50': step < 3, 'text-success': step > 3 }">{{ $t('steps.step3') }}</span>
          </div>
        </nav>

        <router-view @posModeChanged="updateCssClass"></router-view>
      </div>
      <advertisement-visual
          v-if="event && event.advertisement && cssClass === 'portal'"
          :advertisement="event.advertisement"
      />
      <select-language/>
    </div>
  </main>
</template>

<style lang="scss">
@media screen {
  @import './sass/app';
  @import './sass/pos';

  div.print-content {
    display: none;
  }
}

@media print {
  .screen-content {
    display: none;
  }

  @import './sass/ticket';
}
</style>

<script>
import LockerCompanyLogo from './components/LockerCompanyLogo';
import SelectLanguage from './components/SelectLanguage';
import AdvertisementVisual from './components/AdvertisementVisual';
import posMixin from './mixins/posMixin'
import darkModeMixin from './mixins/darkModeMixin'
import EventBus from '@/eventBus';
import {clearLanguageSession, getLanguage, setLocale} from '@/i18n';
import translationMixin from './mixins/translationMixin';

export default {
  mixins: [posMixin, translationMixin, darkModeMixin],
  components: {
    LockerCompanyLogo,
    SelectLanguage,
    AdvertisementVisual
  },
  provide() {
    return {
      $zone: () => this.zone
    }
  },

  data() {
    return {
      cssClass: 'portal',
      event: null,
      booking: null
    }
  },

  watch: {
    // Watch the route and reset the locale when we navigate to booking.
    '$route'(to) {
      if (to.name === 'booking') {
        EventBus.$emit('resetLocale');
      }
    }
  },

  computed: {
    locale() {
      return getLanguage();
    },
    /**
     * The zone from the QR code the user has scanned.
     *
     * @return {String|null}
     */
    zone() {
      if (typeof this.$route.query.zone !== 'undefined') {
        return this.$route.query.zone;
      }

      return null;
    },
    /**
     * The active step for the step navigation based on the route name
     *
     * @return {String|null}
     */
    step() {
      if (this.$route.name === 'booking' || this.$route.name === 'zones') {
        return 1;
      }

      if (this.$route.name === 'lockers') {
        return 2;
      }

      if (this.$route.name === 'details' || this.$route.name === 'payment') {
        return 3;
      }

      return 1;
    },
    /**
     * Get total amount.
     *
     * @return Number
     */
    totalAmount() {
      return this.booking && this.event ? this.booking.calculateTotalAmount(this.event) : 0;
    },
    /**
     * Show the step navigation of not.
     *
     * @return Boolean
     */
    showSteps() {
      if (this.inPosMode()) {
          return false;
      }

      return this.$route.name !== 'notfound' && this.$route.name !== 'confirmation' && this.$route.name !== 'home'
    }
  },

  created() {
    this.updateCssClass();
  },

  mounted() {
    EventBus.$on('resetLocale', () => {
      clearLanguageSession();
      setLocale(getLanguage())
    });

    EventBus.$on('setEvent', (event) => {
      this.event = event;
    });

    EventBus.$on('setBooking', (booking) => {
      this.booking = booking;
    });

    this.updateDarkMode();
  },

  methods: {
    /**
     * Update css class according to the pos mode.
     */
    updateCssClass: function () {
      this.cssClass = this.inPosMode() ? 'pos' : 'portal';

      this.$nextTick(() => {
        document.documentElement.classList.add(this.cssClass);
      })
    },
    /**
     * Update dark mode according to a query parameter.
     */
    updateDarkMode: function() {
      if(this.inDarkMode()) {
        this.$nextTick(() => {
          document.documentElement.setAttribute('data-bs-theme', 'dark');
        })
      }
    }
  },
}
</script>

